import { BrowserRouter as Router, Switch } from "react-router-dom";
import Path from "./enums/path";
import AuthPage from "./pages/auth.page";
import AuthorizedPage from "./pages/authorized.page";
import AuthRoute from "./routes/auth.route";
import AuthorizedRoute from "./routes/authorized.route";
import { AppContext } from "contexts";
import { ErrorModal } from "components/modals";
import React, { useState, useCallback } from "react";
import { useModal } from "hooks";
import { Image } from "components/commons";
import { CautionSign } from "images";
import CssBaseline from "@material-ui/core/CssBaseline";
import { StylesProvider } from "@material-ui/core/styles";
import ErrorPage from "pages/error.page";
import { CarCrash } from "images";
import UnauthorizedPage from "pages/unauthorized.page";
import firebase from "firebase/compat/app";
import "firebase/compat/app-check";
import env from "environments/env";
import useBeforeMount from "hooks/useBeforeMount";
import FeatureFlagPage from "pages/featureflag.page";
import AppLoader from "components/commons/app-loader/app-loader";

function App() {
  const [appState, setAppState] = useState({ loading: false });
  const errorModal = useModal();
  const [pageError, setPageError] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const toggleLoading = useCallback((loading) => {
    setAppState((state) => {
      return { ...state, loading };
    });
  }, []);

  useBeforeMount(async () => {
    if (!firebase.apps.length) {
      firebase.initializeApp(env.FIREBASE_CONFIG);
    }

    const isLocalhost =
      typeof window !== "undefined" && ["127.0.0.1", "[::1]"].includes(window.location.hostname);

    if (isLocalhost && typeof window !== "undefined") {
      const storedDebugToken = window.localStorage.getItem("FIREBASE_APPCHECK_DEBUG_TOKEN") || "";
      const configuredDebugToken =
        (typeof env.FIREBASE_APPCHECK_DEBUG_TOKEN === "string" &&
          env.FIREBASE_APPCHECK_DEBUG_TOKEN.trim()) ||
        storedDebugToken;

      if (configuredDebugToken) {
        window.FIREBASE_APPCHECK_DEBUG_TOKEN = configuredDebugToken;
      } else {
        window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
    }

    // App Check is non-blocking for app startup; if unavailable we still allow login flow.
    if (env.RECAPTCHA_SITE_KEY) {
      try {
        const appCheck = firebase.appCheck();
        appCheck.activate(env.RECAPTCHA_SITE_KEY, true);

        if (isLocalhost) {
          // Force token exchange once so debug-token flow is visible in Network and Console.
          appCheck
            .getToken(true)
            .then((result) => {
              if (result?.token) {
                console.info("Firebase App Check debug token exchange succeeded.");
              }
            })
            .catch((error) => {
              console.warn("Firebase App Check debug token exchange failed", error);
            });
        }
      } catch (error) {
        console.warn("Firebase App Check initialization failed", error);
      }
    }

    setInitializing(false);
  });

  return (
    <FeatureFlagPage>
      {initializing ? (
        <AppLoader />
      ) : (
        <AppContext.Provider
          value={{
            toggleLoading,
            errorModal,
            appState,
            pageError,
            setPageError,
            errorStatus,
            setErrorStatus,
          }}
        >
          <StylesProvider injectFirst>
            <ErrorModal {...errorModal} />
            <CssBaseline />
            <div className="App">
              {pageError ? (
                errorStatus === 403 || errorStatus === 401 ? (
                  <UnauthorizedPage />
                ) : (
                  <ErrorPage />
                )
              ) : (
                <Router>
                  <Switch>
                    <AuthRoute path={Path.Auth} component={AuthPage} />
                    <AuthorizedRoute path={Path.Slash} component={AuthorizedPage} />
                  </Switch>
                </Router>
              )}
            </div>
          </StylesProvider>
          <div className="render-offline-images">
            <Image src={CautionSign} />
            <Image src={CarCrash} />
          </div>
        </AppContext.Provider>
      )}
    </FeatureFlagPage>
  );
}

export default App;
